// UI-5: "Assign to" in the Add task modal (mockup add-task-modal.html). The chosen people as
// chips, and a search over people AND departments. A department is not stored: choosing one adds
// everyone in it (the members' own department field, from /api/team/members), so assignedTeam
// stays a list of user ids, exactly as before. On a phone the search opens in a sheet so the
// keyboard and the results never fight over the screen; elsewhere the results drop below.
import React, { useMemo, useState } from 'react';
import { Avatar, Sheet, SearchInput, useBreakpoint } from '../../ui';

function Results({ people, departments, chosen, onPick, onPickDepartment, query }) {
  const none = people.length === 0 && departments.length === 0;
  return (
    <div className="ui-assign__results" role="listbox" aria-label="People and departments" aria-multiselectable="true">
      {people.length > 0 && (
        <div role="group" aria-label="People">
          <span className="ui-assign__grouplabel" aria-hidden="true">People</span>
          {people.map((m) => (
            <button key={m.id} type="button" role="option" aria-selected={chosen.has(String(m.id))} className="ui-assign__option"
              onClick={() => onPick(m)}>
              <Avatar name={m.name} size={24} color="pastel-lilac" />
              <span className="ui-assign__name">{m.name}</span>
              {m.department && <span className="ui-assign__sub">{m.department}</span>}
              {chosen.has(String(m.id)) && <span className="ui-assign__tick" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
      {departments.length > 0 && (
        <div role="group" aria-label="Departments">
          <span className="ui-assign__grouplabel" aria-hidden="true">Departments</span>
          {departments.map((d) => (
            <button key={d.name} type="button" role="option" aria-selected={d.all} className="ui-assign__option"
              onClick={() => onPickDepartment(d)}>
              <span className="ui-assign__dept" aria-hidden="true">👥</span>
              <span className="ui-assign__name">{d.name}</span>
              <span className="ui-assign__sub">{d.members.length} {d.members.length === 1 ? 'person' : 'people'}</span>
              {d.all && <span className="ui-assign__tick" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
      {none && <p className="ui-assign__none">{query ? 'No one matches.' : 'No one to assign yet.'}</p>}
    </div>
  );
}

export default function AssignPicker({ members = [], value = [], onChange, currentUserId }) {
  const bp = useBreakpoint();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const chosen = useMemo(() => new Set(value.map(String)), [value]);

  const q = query.trim().toLowerCase();
  const people = members.filter((m) => !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  const departments = useMemo(() => {
    const by = new Map();
    for (const m of members) if (m.department) by.set(m.department, [...(by.get(m.department) || []), m]);
    return [...by.entries()].map(([name, list]) => ({ name, members: list, all: list.every((m) => chosen.has(String(m.id))) }));
  }, [members, chosen]);
  const depts = departments.filter((d) => !q || d.name.toLowerCase().includes(q));

  const toggle = (m) => {
    const id = String(m.id);
    onChange(chosen.has(id) ? value.filter((x) => String(x) !== id) : [...value, id]);
  };
  const pickDepartment = (d) => {
    const ids = d.members.map((m) => String(m.id));
    onChange(d.all ? value.filter((x) => !ids.includes(String(x))) : [...value, ...ids.filter((id) => !chosen.has(id))]);
  };
  const selected = members.filter((m) => chosen.has(String(m.id)));
  const label = (m) => (String(m.id) === String(currentUserId) ? 'You' : m.name);
  const phone = bp === 'phone';
  const results = <Results people={people} departments={depts} chosen={chosen} onPick={toggle} onPickDepartment={pickDepartment} query={q} />;

  return (
    <div className="ui-assign">
      <div className="ui-assign__box">
        {selected.map((m) => (
          <span key={m.id} className="ui-assign__chip">
            <Avatar name={m.name} size={24} color="pastel-lilac" />
            <span>{label(m)}</span>
            <button type="button" className="ui-assign__remove ui-hit" aria-label={`Remove ${m.name}`} onClick={() => toggle(m)}>×</button>
          </span>
        ))}
        {phone ? (
          <button type="button" className="ui-assign__find" onClick={() => setOpen(true)}>+ Find a person or department</button>
        ) : (
          <input className="ui-assign__input" value={query} placeholder="+ Find a person or department" aria-label="Find a person or department"
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } if (e.key === 'Enter') e.preventDefault(); }} />
        )}
      </div>
      {!phone && open && <div className="ui-assign__drop" onMouseDown={(e) => e.preventDefault()}>{results}</div>}
      {phone && (
        <Sheet open={open} onClose={() => { setOpen(false); setQuery(''); }} title="Assign to">
          <SearchInput label="Find a person or department" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a person or department" />
          {results}
        </Sheet>
      )}
    </div>
  );
}
